// Bulk family licences — turning a pasted list or a CSV file into the emails
// grant_family_sponsorships_bulk receives (migration 20261023000013).
//
// THE SERVER IS THE AUTHORITY; THIS IS THE PREVIEW. The RPC trims, lower-cases,
// de-duplicates and validates again, and answers one row per distinct email. The
// client does the same work first only so the admin sees "Give 38 licences, 2
// are not emails, 3 were repeated" BEFORE anything is given, and so a file of
// 5000 rows is refused here instead of half-sent.
//
// Nothing is dropped silently: every distinct entry is either a valid email
// (sent) or listed as not an email (shown in the results as such).
//
// Adult emails only. Pure TS, no dependencies.

/** The RPC refuses more than this many emails in one call. */
export const BULK_EMAIL_MAX = 2000;

/** The same rule as app_valid_email (migration 0013), so preview and server agree. */
export function isValidEmail(email: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) && email.length <= 254;
}

export interface ParsedEmails {
  /** Valid, trimmed, lower-cased, de-duplicated, in first-seen order. */
  emails: string[];
  /** Distinct entries that are not emails (trimmed, lower-cased), in order. */
  invalid: string[];
  /** How many entries repeated an earlier one and were folded into it. */
  duplicates: number;
  /** More distinct entries than one upload may hold: nothing should be sent. */
  overCap: boolean;
}

/** Folds raw entries into a {@link ParsedEmails}. Blank entries are skipped. */
export function foldEntries(raw: readonly string[]): ParsedEmails {
  const seen = new Set<string>();
  const emails: string[] = [];
  const invalid: string[] = [];
  let duplicates = 0;
  for (const entry of raw) {
    const e = entry.trim().replace(/^mailto:/i, "").toLowerCase();
    if (e === "") continue;
    if (seen.has(e)) {
      duplicates++;
      continue;
    }
    seen.add(e);
    (isValidEmail(e) ? emails : invalid).push(e);
  }
  return { emails, invalid, duplicates, overCap: emails.length + invalid.length > BULK_EMAIL_MAX };
}

/** A pasted list: one per line, or separated by commas, semicolons or tabs. */
export function parseEmailList(text: string): ParsedEmails {
  return foldEntries(text.split(/[\r\n,;\t]+/));
}

/** Splits one CSV line into cells, honouring "quoted, cells" and "" escapes. */
export function csvCells(line: string): string[] {
  const cells: string[] = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (ch === '"') {
        quoted = false;
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === "," || ch === ";" || ch === "\t") {
      cells.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  cells.push(cur);
  return cells.map((c) => c.trim());
}

const EMAIL_HEADER = /^(e-?mail|email address|e-mail address|parent email|parent e-?mail)$/i;

/**
 * A CSV file: the column headed "email" (any case) when there is one, else the
 * first column. A first row that names an email column, or whose first cell is
 * not an address at all, is a header and is skipped.
 */
export function parseEmailCsv(text: string): ParsedEmails {
  const rows = text
    .replace(/^﻿/, "")
    .split(/\r\n|\n|\r/)
    .filter((l) => l.trim() !== "")
    .map(csvCells);
  if (rows.length === 0) return foldEntries([]);
  const header = rows[0];
  const named = header.findIndex((c) => EMAIL_HEADER.test(c));
  const col = named >= 0 ? named : 0;
  const skipFirst = named >= 0 || !(header[0] ?? "").includes("@");
  const body = skipFirst ? rows.slice(1) : rows;
  return foldEntries(body.map((r) => r[col] ?? ""));
}
